import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import SwiperCore, { Navigation, Autoplay, SwiperOptions } from 'swiper';
import { SwiperModule } from 'swiper/angular';
import { environment } from 'src/environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { WebsocketService } from 'src/app/core/services/websocket.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/core/services/api.service';
import { Observable, combineLatest, map, startWith, tap } from 'rxjs';
import { FiatValuePipe } from '../../pipes/fiat-value.pipe';
import { IxfiCurrencyService } from 'header';
import { getPrecision } from 'src/app/core/functions/utility';
import { AbsValuePipe } from '../../pipes/abs-value.pipe';
import { ImageComponent } from '../image/image.component';
SwiperCore.use([Navigation, Autoplay]);

interface TrendingCoin {
    coin_id: string;
    code: string;
    name: string;
    price: number;
    change?: number;
    icon: string;
    is_up: boolean;
    isKLineUnavailable: boolean; // frontend parameter
    precision: number; // frontend parameter
}

@Component({
    selector: 'app-trending',
    standalone: true,
    imports: [CommonModule, SwiperModule, TranslateModule, FiatValuePipe, AbsValuePipe, ImageComponent],
    template: `
        <div class="container" [ngClass]="{ 'p-0': isInnerPage }" *ngIf="trendingObs | async">
            <h2 class="py-2 subheading">
                <span>{{ 'GENERAL.TRENDING' | translate }}</span>
                {{ 'GENERAL.COINS' | translate }}
            </h2>
            <div class="summary-block pb-3 mb-3">
                <swiper [config]="swipeConfig" class="mySwiper">
                    <ng-template swiperSlide *ngFor="let item of trendingCoins; trackBy: trackByFn">
                        <div class="card summary-card  mx-1 pointer" (click)="showCoinDetail(item)">
                            <div class="d-flex align-content-start assetsBlock mb-2 p-3">
                                <div class="assets-coin">
                                    <span class="single-coin">
                                        <app-image
                                            [imgWidth]="24"
                                            [imgHeight]="24"
                                            [imgStyle]="{ 'border-radius': '35px' }"
                                            [imgSrc]="'coins/' + item.code + '.png'"
                                            class="d-flex">
                                        </app-image>
                                    </span>
                                </div>
                                <div class="d-flex flex-column">
                                    <span>{{ item.code | uppercase }}</span>
                                    <strong>
                                        <div class="d-inline-flex">
                                            {{
                                                item.price
                                                    | fiatValue : usdExchangeRate
                                                    | currency : currencySymbol : 'symbol' : '1.0-' + item.precision
                                            }}
                                        </div>
                                    </strong>
                                    <div class="d-inline-flex asset">
                                        <span [ngClass]="item.is_up ? 'text-success' : 'text-danger'">
                                            <em
                                                [ngClass]="
                                                    item.is_up
                                                        ? 'flaticon-two-arrows-pointing-up'
                                                        : 'flaticon-two-down-arrows'
                                                "
                                                style="font-size: 10px">
                                            </em>
                                            <span class="mx-1">
                                                <span>{{ item.change ?? 0 | absValue | number : '1.2-2' }}%</span>
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <img
                                *ngIf="!item.isKLineUnavailable"
                                (error)="onImageError(item)"
                                height="40"
                                width="156"
                                [src]="
                                    GRAPH_CDN_URL +
                                    'coin-k-lines/' +
                                    item.code.toLowerCase() +
                                    getGraphColor(item) +
                                    '_30d.svg'
                                "
                                class="graph-icon-img"
                                alt="Graph Unavailable" />
                            <div
                                *ngIf="item.isKLineUnavailable"
                                class="d-flex align-self-center"
                                style=" font-size: 0.875rem">
                                {{ 'GENERAL.CHART_UNAVAILABLE' | translate }}
                            </div>
                        </div>
                    </ng-template>
                </swiper>
            </div>
        </div>
    `,
    styleUrls: ['./trending.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrendingComponent implements OnInit {
    isInnerPage!: boolean;
    CDN_URL = environment.CDN_URL;
    GRAPH_CDN_URL = environment.GRAPH_CDN_URL;

    trendingCoins: TrendingCoin[] = [];

    swipeConfig: SwiperOptions = {
        loop: true,
        spaceBetween: 2,
        slidesPerView: 6,
        navigation: true,
        centeredSlides: true,
        breakpoints: {
            1200: {
                slidesPerView: 6
            },
            1024: {
                slidesPerView: 4
            },
            800: {
                slidesPerView: 3
            },
             700: {
                slidesPerView: 2.5
            },
            410: {
                slidesPerView: 2
            },
            100: {
                slidesPerView: 1.1
            }
        }
    };

    currencySymbol: string = 'USD';
    usdExchangeRate: number = 1;

    trendingObs!: Observable<boolean>;

    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private apiService = inject(ApiService);
    private websocketService = inject(WebsocketService);
    private currencyService = inject(IxfiCurrencyService);

    ngOnInit(): void {
        const trendingCoinsHttp$: Observable<TrendingCoin[]> = this.apiService
            .fetch<TrendingCoin[]>('TRENDING_COINS')
            .pipe(
                map(response => {
                    response = response.map(item => ({
                        ...item,
                        price: item.price ? item.price : 0,
                        precision: getPrecision(item.price)
                    }));
                    /**
                     * Check if the component is displayed in the inner page or the landing page
                     * If true, remove the inner coin from trending coins array
                     */
                    const innerPageCoin: string | null = this.route.snapshot.paramMap.get('coinCode');
                    this.isInnerPage = innerPageCoin ? true : false;
                    if (this.isInnerPage) {
                        const indexToRemove: number = response.findIndex(
                            coin => coin.code.toUpperCase() === innerPageCoin?.toUpperCase()
                        );
                        response.splice(indexToRemove, 1);
                    }
                    /* Shuffle the trending coins */
                    for (let i = response.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [response[i], response[j]] = [response[j], response[i]];
                    }
                    return response;
                }),
                tap(response => (this.trendingCoins = response))
            );
        const socket$ = this.websocketService.coinInfoObs.pipe(
            startWith(null),
            tap(response => {
                if (response) {
                    const index: number = this.trendingCoins.findIndex(coin => coin.coin_id === response.coin_id);
                    if (index !== -1) {
                        this.trendingCoins[index].is_up = response.is_up;
                        this.trendingCoins[index].change = response.change;
                        this.trendingCoins[index].price = response.coin_price_usd;
                        this.trendingCoins[index].precision = getPrecision(this.trendingCoins[index].price);
                    }
                }
            })
        );

        const selectedCurrency$ = this.currencyService.currencyObs.pipe(
            map(response => response.selectedCurrency),
            tap(selectedCurrency => {
                this.currencySymbol = selectedCurrency.currency_symbol.toUpperCase();
                this.usdExchangeRate = this.currencySymbol === 'USD' ? 1 : selectedCurrency.usd_exchange_rate;
            })
        );

        this.trendingObs = combineLatest([trendingCoinsHttp$, socket$, selectedCurrency$]).pipe(map(_ => true));
    }

    /**
     * This function is called when user clicks on the trending coin
     * If user is on inner page, reload the page of the clicked trending coin to load ITB Data for new Coin
     * Else, navigate the user to the particular inner page of the selected trending coin
     * @param trendingCoin Navigate to Detail Page on the trending coin
     */
    showCoinDetail(trendingCoin: TrendingCoin) {
        if (this.isInnerPage) {
            if (trendingCoin.code.toUpperCase() === this.route.snapshot.paramMap.get('coinCode')?.toUpperCase()) {
                this.router.navigate(['/coins', trendingCoin.code.toUpperCase()]);
            } else window.location.href = environment.IXFI_RESEARCH + '/coins/' + trendingCoin.code.toUpperCase();
        } else {
            this.router.navigate(['/coins', trendingCoin.code.toUpperCase()]);
        }
    }

    onImageError(trendingCoin: TrendingCoin) {
        trendingCoin.isKLineUnavailable = true;
    }

    trackByFn(index: number, item: TrendingCoin) {
        return item.coin_id;
    }

    getGraphColor(coin: TrendingCoin): string {
        if (coin.is_up) return '_green';
        else return '_red';
    }
}
